'use client';

import { c as _c } from "react/compiler-runtime";
import { useState, useCallback, type PropsWithChildren } from 'react';
import type { PanelTypes } from '@/components/panels/panelRegistry';
import { WorkspaceContext } from '@/contexts/workspaceContext';
import { PanelSize, type SidePanelSize, type DashboardAiEntryStore, type DialogEntry, type DialogOptions, type DialogStore, type SnackbarOptions, type SnackbarStore, type SidePanelStore } from './workspaceContextTypes';
const DEFAULT_SNACKBAR_AUTO_HIDE_DURATION = 3000;
const getSnackbarAutoHideDuration = (options?: SnackbarOptions) => options?.autoHideDuration === undefined ? DEFAULT_SNACKBAR_AUTO_HIDE_DURATION : options.autoHideDuration;
export const WorkspaceProvider = t0 => {
  const $ = _c(20);
  const {
    children
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = [];
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const [dialogStack, setDialogStack] = useState(t1);
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = (id, content, options) => {
      setDialogStack(stack => [...stack, {
        isLoading: false,
        ...options,
        id,
        content
      }]);
    };
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const openDialog = t2;
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = () => {
      setDialogStack(_temp);
    };
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const backDialog = t3;
  let t4;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = () => {
      setDialogStack([]);
    };
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  const closeAllDialogs = t4;
  let t5;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = (id_0, content_0, options_0) => {
      setDialogStack(stack_1 => [...stack_1.slice(0, -1), {
        isLoading: false,
        ...options_0,
        id: id_0,
        content: content_0
      }]);
    };
    $[4] = t5;
  } else {
    t5 = $[4];
  }
  const replaceDialog = t5;
  let t6;
  if ($[5] !== dialogStack) {
    t6 = id_1 => {
      if (id_1) {
        return dialogStack.some(d => d.id === id_1);
      }
      return dialogStack.length > 0;
    };
    $[5] = dialogStack;
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  const isDialogOpen = t6;
  let t7;
  if ($[7] !== dialogStack || $[8] !== isDialogOpen) {
    t7 = {
      stack: dialogStack,
      open: openDialog,
      back: backDialog,
      closeAll: closeAllDialogs,
      replace: replaceDialog,
      isOpen: isDialogOpen
    };
    $[7] = dialogStack;
    $[8] = isDialogOpen;
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  const sharedDialog = t7;
  const [rightPanel, t8] = useState({
    isOpen: false,
    isAbsolute: false,
    isResizable: false,
    Component: undefined,
    panelSize: PanelSize.Large,
    setSize: panelSize => setRightPanel(state => ({
      ...state,
      panelSize
    })),
    setResizedWidth: resizedWidth => setRightPanel(state_0 => ({
      ...state_0,
      resizedWidth
    })),
    setIsOpen: (isOpen, panelSize_0, isAbsolute, isResizable) => setRightPanel(state_1 => ({
      ...state_1,
      isOpen,
      isAbsolute: isAbsolute ?? false,
      isResizable: isOpen ? isResizable ?? false : state_1.isResizable,
      panelSize: isOpen ? panelSize_0 ?? PanelSize.Large : state_1.panelSize
    })),
    setComponent: Component => setRightPanel(state_2 => ({
      ...state_2,
      Component
    }))
  });
  const setRightPanel = t8;
  const [snackbar, t9] = useState({
    isOpen: false,
    message: "",
    notificationId: 0,
    variant: "default",
    autoHideDuration: DEFAULT_SNACKBAR_AUTO_HIDE_DURATION,
    showMessage: (message, options_1) => setSnackbar(state_3 => ({
      ...state_3,
      isOpen: true,
      message,
      notificationId: state_3.notificationId + 1,
      variant: "default",
      action: options_1?.action,
      autoHideDuration: getSnackbarAutoHideDuration(options_1)
    })),
    showErrorMessage: (message_0, options_2) => setSnackbar(state_4 => ({
      ...state_4,
      isOpen: true,
      message: message_0,
      notificationId: state_4.notificationId + 1,
      variant: "error",
      action: options_2?.action,
      autoHideDuration: getSnackbarAutoHideDuration(options_2)
    })),
    showWarningMessage: (message_1, options_3) => setSnackbar(state_5 => ({
      ...state_5,
      isOpen: true,
      message: message_1,
      notificationId: state_5.notificationId + 1,
      variant: "default",
      action: options_3?.action,
      autoHideDuration: getSnackbarAutoHideDuration(options_3)
    })),
    hide: () => setSnackbar(_temp2)
  });
  const setSnackbar = t9;
  const [dashboardAiEntryVisible, setDashboardAiEntryVisible] = useState(false);
  let t10;
  if ($[10] !== dashboardAiEntryVisible) {
    t10 = {
      isVisible: dashboardAiEntryVisible,
      setIsVisible: setDashboardAiEntryVisible
    };
    $[10] = dashboardAiEntryVisible;
    $[11] = t10;
  } else {
    t10 = $[11];
  }
  const dashboardAiEntry = t10;
  let t11;
  if ($[12] !== dashboardAiEntry || $[13] !== rightPanel || $[14] !== sharedDialog || $[15] !== snackbar) {
    t11 = {
      rightPanel,
      snackbar,
      sharedDialog,
      dashboardAiEntry
    };
    $[12] = dashboardAiEntry;
    $[13] = rightPanel;
    $[14] = sharedDialog;
    $[15] = snackbar;
    $[16] = t11;
  } else {
    t11 = $[16];
  }
  let t12;
  if ($[17] !== children || $[18] !== t11) {
    t12 = <WorkspaceContext.Provider value={t11}>{children}</WorkspaceContext.Provider>;
    $[17] = children;
    $[18] = t11;
    $[19] = t12;
  } else {
    t12 = $[19];
  }
  return t12;
};
function _temp(stack_0) {
  return stack_0.slice(0, -1);
}
function _temp2(state_6) {
  return {
    ...state_6,
    isOpen: false,
    message: "",
    variant: "default",
    action: undefined,
    autoHideDuration: DEFAULT_SNACKBAR_AUTO_HIDE_DURATION
  };
}